import { useParams, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { videos } from '@/mocks/data';

function formatViews(n: number): string {
  if (n >= 10000) return (n / 10000).toFixed(1) + '万';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return String(n);
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const video = useMemo(() => videos.find((v) => v.id === Number(id)), [id]);
  const relatedVideos = useMemo(
    () =>
      video
        ? videos.filter((v) => v.categorySlug === video.categorySlug && v.id !== video.id).slice(0, 4)
        : [],
    [video],
  );

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">视频不存在</h1>
            <button
              onClick={() => navigate('/')}
              className="text-primary-500 hover:underline text-sm cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-4 min-h-screen">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: video.category, path: `/category/${video.categorySlug}` },
              { label: video.title },
            ]}
          />

          <article className="mt-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 min-w-0">
                <div className="relative aspect-video rounded-lg overflow-hidden bg-background-100 mb-6">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-white cursor-pointer hover:bg-primary-600 transition-colors">
                      <i className="ri-play-fill text-3xl md:text-4xl ml-1"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-2 py-1 rounded">
                    {video.duration}
                  </div>
                </div>

                <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-3">
                  {video.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 flex-wrap">
                  <span
                    className="text-primary-500 font-medium cursor-pointer hover:underline whitespace-nowrap"
                    onClick={() => navigate(`/category/${video.categorySlug}`)}
                  >
                    {video.category}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line"></i>
                    {formatViews(video.views)} 次观看
                  </span>
                  <span className="whitespace-nowrap">{video.date}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {video.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: video.content }}
                />

                <div className="mt-8 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <p className="text-xs text-foreground-500">
                    <strong className="text-foreground-700">声明：</strong>
                    本文内容来源于网络公开信息，仅供娱乐参考。如有不实之处，请及时联系我们进行更正或删除。
                  </p>
                </div>
              </div>

              <div className="lg:w-[340px] shrink-0">
                <h3 className="text-base font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-links-line text-primary-500"></i>
                  相关视频
                </h3>
                <div className="flex flex-col gap-4">
                  {relatedVideos.map((rv) => (
                    <div
                      key={rv.id}
                      className="flex gap-3 cursor-pointer group"
                      onClick={() => { navigate(`/article/${rv.id}`); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    >
                      <div className="w-28 h-20 shrink-0 rounded-lg overflow-hidden bg-background-100">
                        <img
                          src={rv.thumbnail}
                          alt={rv.title}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                          {rv.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                          <span>{formatViews(rv.views)} 次观看</span>
                          <span>{rv.date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: video.title,
            description: video.description,
            datePublished: video.date,
            author: {
              '@type': 'Organization',
              name: '黑料视频',
            },
            publisher: {
              '@type': 'Organization',
              name: '黑料视频',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://heiliaoshipin.com/article/${video.id}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://heiliaoshipin.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: video.category,
                item: `https://heiliaoshipin.com/category/${video.categorySlug}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: video.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}